import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonStyleOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-electric text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_14px_34px_-14px_rgb(44_92_255/0.95)] hover:bg-electric-bright hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.24),0_18px_44px_-14px_rgb(91_130_255/0.95)]',
    secondary: 'border border-white/15 bg-white/[0.03] text-foreground hover:border-white/35 hover:bg-white/[0.07]',
    ghost: 'text-foreground hover:text-electric-soft'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-10 px-4 text-sm',
    md: 'h-12 px-6 text-[0.9375rem]',
    lg: 'h-14 px-7 text-base'
}

export const buttonStyles = ({ variant = 'primary', size = 'md', className }: ButtonStyleOptions = {}): string => cn(
    'group/button relative inline-flex shrink-0 items-center justify-center gap-2.5 whitespace-nowrap rounded-full font-medium tracking-[-0.01em] outline-none transition-[background-color,border-color,color,box-shadow] duration-300 ease-smooth focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60',
    VARIANTS[variant],
    variant === 'ghost' ? 'h-auto px-0' : SIZES[size],
    className
)

export const ButtonArrow: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <ArrowRight
            aria-hidden='true'
            className={cn('size-4 transition-transform duration-300 ease-smooth group-hover/button:translate-x-1', className)}
        />
    )
}

type ButtonLinkProps = ComponentProps<typeof Link> & ButtonStyleOptions & {
    withArrow?: boolean
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant,
    size,
    className,
    withArrow = false,
    children,
    ...props
}): ReactNode => {
    return (
        <Link className={buttonStyles({ variant, size, className })} {...props}>
            {children}
            {withArrow && <ButtonArrow />}
        </Link>
    )
}
